import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { MODAL_ROUTES } from "~/router/constants/routes";
import { useNavigateModal } from "~/router/useNavigateModal";
import { convertTo12HourFormat } from "~/screens/Scheduling/InitialVisit/utils/date-time-utils";
import { VISIT_TYPES, type VisitType } from "~/services";
import type { AdminScheduleVisitResponse } from "~/services/scheduling/types";
import { usePageTitle } from "~/shared";
import type { ModalProps } from "~/shared.types";
import { customIcons, Modal } from "~/ui";
import { toast } from "~/ui/admin/toast/toast";
import { getVisitTypeTitle } from "~/utils";
import { CloseConfirmationDialog } from "./components/close-confirmation-dialog";
import { StaffBookingFooter } from "./components/staff-booking-footer";
import { StaffBookingHeader } from "./components/staff-booking-header";
import { StepHeading } from "./components/step-heading";
import { useStaffBookingPatient } from "./hooks/use-staff-booking-patient";
import { useStartStaffBookingFlow } from "./hooks/use-start-staff-booking-flow";
import { STAFF_BOOKING_STEPS } from "./staff-booking-types";
import { CoachPricingStep } from "./steps/coach-pricing-step";
import { EmailStep } from "./steps/email-step";
import { PatientInfoStep } from "./steps/patient-info-step";
import { ProviderSelectionStep } from "./steps/provider-selection-step";
import { SlotStep } from "./steps/slot-step";
import { StateInsuranceStep } from "./steps/state-insurance-step";
import { VisitTypeStep } from "./steps/visit-type-step";
import { useStaffBookingSteps } from "./use-staff-booking-steps";
import { getBookingConfirmedMessage } from "./utils/get-booking-confirmed-message";

export const StaffBookingDrawer = ({ show, onClose }: ModalProps) => {
  const { userId = null } = useParams<{ userId: string }>();
  const location = useLocation();
  const hasPreviousEntry =
    location.key !== "default" &&
    !!(location.state as { previousLocation?: unknown } | null)
      ?.previousLocation;
  const { step, data, next, back, update } = useStaffBookingSteps(userId);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [opener] = useState(() => document.activeElement);

  const {
    data: patient,
    isPending: isPatientPending,
    isError: isPatientError,
    refetch: refetchPatient,
  } = useStaffBookingPatient(userId);

  const startBookingFlow = useStartStaffBookingFlow();
  const navigateModal = useNavigateModal();
  const stepContainerRef = useRef<HTMLDivElement>(null);
  const isInitialStep = useRef(true);

  usePageTitle(data.visitType ? getVisitTypeTitle(data.visitType) : "", {
    restoreOnUnmount: true,
  });

  useEffect(() => {
    if (isInitialStep.current) {
      isInitialStep.current = false;
      return;
    }

    stepContainerRef.current?.querySelector("h2")?.focus();
  }, [step]);

  const handleClose = () => {
    if (isBooking) return;

    if (step === STAFF_BOOKING_STEPS.PATIENT_INFO) {
      setShowCloseConfirmation(true);
      return;
    }

    onClose();
  };

  const handleEmailContinue = (email: string) => {
    startBookingFlow(VISIT_TYPES.INITIAL);
    next(STAFF_BOOKING_STEPS.STATE_INSURANCE, {
      email,
      visitType: VISIT_TYPES.INITIAL,
    });
  };

  const handleVisitTypeSelect = (visitType: VisitType) => {
    startBookingFlow(visitType);
    next(STAFF_BOOKING_STEPS.STATE_INSURANCE, {
      visitType,
      stateId: data.stateId ?? patient?.context.lastAppointmentStateId ?? null,
      ...(visitType !== data.visitType && {
        insuranceId: null,
        providerId: null,
        appointmentDetails: null,
        patientInfo: null,
      }),
    });
  };

  const handleStateInsuranceContinue = () => {
    if (data.visitType === VISIT_TYPES.FOLLOW_UP) {
      next(STAFF_BOOKING_STEPS.PROVIDER_SELECTION);
      return;
    }

    next(
      data.visitType === VISIT_TYPES.COACH
        ? STAFF_BOOKING_STEPS.COACH_PRICING
        : STAFF_BOOKING_STEPS.SLOT,
    );
  };

  const email = patient?.user.email ?? data.email;
  const isFollowUp = data.visitType === VISIT_TYPES.FOLLOW_UP;

  const handleBooked = (response: AdminScheduleVisitResponse) => {
    const { appointmentDetails, visitType } = data;

    if (appointmentDetails?.timeSlot && visitType) {
      toast({
        type: "success",
        ...getBookingConfirmedMessage({
          visitType,
          date: appointmentDetails.date,
          time: convertTo12HourFormat(
            appointmentDetails.timeSlot.originalTime ??
              appointmentDetails.timeSlot.startTime,
          ),
          providerName: appointmentDetails.provider?.name ?? "",
          isNewAccount: !userId,
        }),
      });
    }

    if (userId && hasPreviousEntry) {
      onClose();
      return;
    }

    navigateModal(
      `${MODAL_ROUTES.userDetails}/${response.user.id}`,
      hasPreviousEntry ? {} : { previousLocation: undefined },
      { replace: true },
    );
  };

  const renderStep = () => {
    if (userId && isPatientPending) {
      return (
        <div role="status" className="flex h-full items-center justify-center">
          <customIcons.SpinnerIcon
            aria-hidden="true"
            className="text-violet size-10 animate-spin"
          />
          <span className="sr-only">Loading patient</span>
        </div>
      );
    }

    if (userId && isPatientError && !patient) {
      return (
        <div className="flex min-h-full flex-col gap-6">
          <StepHeading title="Book a visit" />
          <p className="text-base text-neutral-700">
            We could not load this patient.{" "}
            <button
              type="button"
              className="text-violet cursor-pointer underline"
              onClick={() => void refetchPatient()}
            >
              Try again
            </button>
          </p>
          <StaffBookingFooter onCancel={handleClose} />
        </div>
      );
    }

    switch (step) {
      case STAFF_BOOKING_STEPS.EMAIL:
        return (
          <EmailStep
            defaultEmail={data.email}
            onCancel={handleClose}
            onContinue={handleEmailContinue}
          />
        );
      case STAFF_BOOKING_STEPS.VISIT_TYPE:
        return (
          <VisitTypeStep
            hasEhrId={!!patient?.user.ehrId}
            onCancel={handleClose}
            onSelect={handleVisitTypeSelect}
          />
        );
      case STAFF_BOOKING_STEPS.STATE_INSURANCE:
        return (
          <StateInsuranceStep
            email={email}
            visitType={data.visitType ?? VISIT_TYPES.INITIAL}
            stateId={data.stateId}
            insuranceId={data.insuranceId}
            registrations={patient?.context.patientRegistrations ?? []}
            onChange={(patch) => update({ ...patch, providerId: null })}
            onContinue={handleStateInsuranceContinue}
            onBack={back}
          />
        );
      case STAFF_BOOKING_STEPS.PROVIDER_SELECTION:
        if (!userId || data.stateId === null) break;

        return (
          <ProviderSelectionStep
            userId={userId}
            stateId={data.stateId}
            insuranceId={data.insuranceId}
            providerId={data.providerId}
            onContinue={(providerId) =>
              next(STAFF_BOOKING_STEPS.SLOT, { providerId })
            }
            onBack={back}
          />
        );
      case STAFF_BOOKING_STEPS.COACH_PRICING:
        if (data.stateId === null) break;

        return (
          <CoachPricingStep
            stateId={data.stateId}
            insuranceId={data.insuranceId}
            onContinue={() => next(STAFF_BOOKING_STEPS.SLOT)}
            onBack={back}
          />
        );
      case STAFF_BOOKING_STEPS.SLOT:
        if (data.stateId === null || !data.visitType) break;
        if (isFollowUp && data.providerId === null) break;

        return (
          <SlotStep
            visitType={data.visitType}
            stateId={data.stateId}
            insuranceId={data.insuranceId}
            providerId={isFollowUp ? data.providerId : null}
            initialSelection={data.appointmentDetails}
            onContinue={(appointmentDetails) =>
              next(STAFF_BOOKING_STEPS.PATIENT_INFO, { appointmentDetails })
            }
            onBack={() => {
              update({ appointmentDetails: null });
              back();
            }}
          />
        );
      case STAFF_BOOKING_STEPS.PATIENT_INFO:
        if (
          data.stateId === null ||
          !data.visitType ||
          !data.appointmentDetails
        ) {
          break;
        }

        return (
          <PatientInfoStep
            userId={userId}
            patient={patient?.user}
            email={email}
            visitType={data.visitType}
            stateId={data.stateId}
            insuranceId={data.insuranceId}
            appointmentDetails={data.appointmentDetails}
            patientInfo={data.patientInfo}
            onBack={(patientInfo) => {
              update({ patientInfo });
              back();
            }}
            onChooseAnotherSlot={(patientInfo) => {
              update({ patientInfo, appointmentDetails: null });
              back();
            }}
            onBookingChange={setIsBooking}
            onBooked={handleBooked}
          />
        );
    }

    return (
      <div className="flex min-h-full flex-col gap-6">
        <StepHeading title="Book a visit" visitType={data.visitType} />
        <p className="text-base text-neutral-700">
          This step could not be loaded. Go back and try again.
        </p>
        <StaffBookingFooter onBack={back} />
      </div>
    );
  };

  return (
    <Modal
      className="gap-8"
      show={show}
      title={userId ? "Book a visit" : "Register a new patient"}
      header={
        <StaffBookingHeader
          user={patient?.user}
          email={step === STAFF_BOOKING_STEPS.EMAIL ? "" : email}
        />
      }
      hideCloseButton
      onClose={handleClose}
      onCloseAutoFocus={(event) => {
        if (!(opener instanceof HTMLElement) || !opener.isConnected) return;
        if (document.querySelector('[role="dialog"][data-state="open"]')) {
          return;
        }

        event.preventDefault();
        opener.focus();
      }}
    >
      <div ref={stepContainerRef} className="h-full">
        {renderStep()}
      </div>

      <CloseConfirmationDialog
        show={showCloseConfirmation}
        onConfirm={() => {
          setShowCloseConfirmation(false);
          onClose();
        }}
        onCancel={() => setShowCloseConfirmation(false)}
      />
    </Modal>
  );
};
